import studentWorkbookApi from '@/api/studentWorkbook.api'
import {
  GroupWorkbook,
  GroupWorkbookPage,
  GroupWorkbookPageDetailColl,
  StudentWorkbook,
  StudentWorkbookPage,
  StudentWorkbookPageDetailColl,
} from '@/dto'
import ExtendedMap from '@/models/ExtendedMap'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

class LessonWorkbookController implements Controller {
  adapter = {
    studentWorkbook: makeAdapter({
      from: (data: res.StudentWorkbook) => {
        return new StudentWorkbook(data)
      },
    }),
    studentWorkbookProgress: makeAdapter({
      from: (data: res.StudentWorkbookProgress) => {
        return new StudentWorkbookPage(data)
      },
    }),
    groupWorkbook: makeAdapter({
      from: (data: res.GroupWorkbook) => {
        return new GroupWorkbook(data)
      },
    }),
    groupWorkbookProgress: makeAdapter({
      from: (data: res.GroupWorkbookProgress) => {
        return new GroupWorkbookPage(data)
      },
    }),
  }

  /*** 학생별 교재 ***/
  // 학생 교재 리스트
  fetchStudentWorkbookColl = async (data: req.StudentWorkbook, pageQueryString: string) => {
    const {
      data: {
        data: { content, pageable, last },
      },
    } = await studentWorkbookApi.getStudentWorkbookList(data, pageQueryString)

    const workbookColl = ExtendedMap.fromArr(content.map(this.adapter.studentWorkbook.from), 'id')

    return {
      workbookColl,
      pageable,
      last,
    }
  }

  // 학생 진도 리스트
  fetchStudentWorkbookPageColl = async (
    params: req.StudentWorkbookProgress,
    pageQueryString: string,
  ) => {
    const {
      data: {
        data: { page },
      },
    } = await studentWorkbookApi.getStudentWorkbookProgress(params, pageQueryString)

    const pageColl = ExtendedMap.fromArr(
      page.content.map(this.adapter.studentWorkbookProgress.from),
      'pageNum',
    )

    return {
      pageColl,
    }
  }

  // 학생 진도별 문제 리스트
  fetchStudentWorkbookPageDetailColl = async (
    data: req.StudentWorkbookPageDetail,
    pageQueryString: string,
  ) => {
    const {
      data: {
        data: { content, pageable },
      },
    } = await studentWorkbookApi.getStudentWorkbookPageDetail(data, pageQueryString)

    const detailColl = new StudentWorkbookPageDetailColl(content)

    return {
      detailColl,
      pageable,
    }
  }

  /*** 학년별 교재 ***/
  // 학년 교재 리스트
  fetchGradeWorkbookColl = async (data: req.GradeWorkbook, pageQueryString: string) => {
    const {
      data: {
        data: { content, pageable, last },
      },
    } = await studentWorkbookApi.getGradeWorkbookList(data, pageQueryString)
    const workbookColl = ExtendedMap.fromArr(content.map(this.adapter.groupWorkbook.from), 'id')

    return {
      workbookColl,
      pageable,
      last,
    }
  }

  // 학년 진도 리스트
  fetchGradeWorkbookPageColl = async (data: req.GradeWorkbookProgress, pageQueryString: string) => {
    const {
      data: {
        data: { content, pageable, last },
      },
    } = await studentWorkbookApi.getGradeWorkbookProgress(data, pageQueryString)

    const pageColl = ExtendedMap.fromArr(
      content.map(this.adapter.groupWorkbookProgress.from),
      'pageNum',
    )

    return { pageColl, pageable, last }
  }

  // 학년 페이지별 문제 리스트
  fetchGradeWorkbookPageDetailColl = async (
    data: req.GradeWorkbookPageDetail,
    pageQueryString: string,
  ) => {
    const {
      data: {
        data: { content },
      },
    } = await studentWorkbookApi.getGradeWorkbookPageDetail<res.GroupPublicWorkbookPageDetail>(
      data,
      pageQueryString,
    )
    const detailColl = new GroupWorkbookPageDetailColl(content)

    return detailColl
  }

  /*** 반별 교재 ***/

  // 반 교재 리스트
  fetchClassWorkbookColl = async (data: req.ClassWorkbook, pageQueryString: string) => {
    const {
      data: {
        data: { content, pageable, last },
      },
    } = await studentWorkbookApi.getClassWorkbookList(data, pageQueryString)

    const workbookColl = ExtendedMap.fromArr(content.map(this.adapter.groupWorkbook.from), 'id')

    return {
      workbookColl,
      pageable,
      last,
    }
  }

  // 반 진도 리스트
  fetchClassWorkbookPageColl = async (data: req.ClassWorkbookProgress, pageQueryString: string) => {
    const {
      data: {
        data: { content, last },
      },
    } = await studentWorkbookApi.getClassWorkbookProgress(data, pageQueryString)

    const pageColl = ExtendedMap.fromArr(
      content.map(this.adapter.groupWorkbookProgress.from),
      'pageNum',
    )

    return { pageColl, last }
  }

  // 반 페이지별 문제 리스트
  fetchClassWorkbookPageDetailColl = async (
    data: req.ClassWorkbookPageDetail,
    pageQueryString: string,
  ) => {
    const {
      data: {
        data: { content },
      },
    } = await studentWorkbookApi.getClassWorkbookPageDetail(data, pageQueryString)

    const detailColl = new GroupWorkbookPageDetailColl(content)

    return detailColl
  }

  unAssignStudentWorkbook = async (workbookIds: StudentWorkbook['id'][]) =>
    await studentWorkbookApi.deleteStudentWorkbook(workbookIds)

  deleteStudentWorkbookRevision = async (revisionIds: number[]) =>
    await studentWorkbookApi.deleteStudentWorkbookRevision(revisionIds)

  assignWorkbookStudent = async (data: req.AddWorkbookToStudent) =>
    await studentWorkbookApi.postAddWorkbookToStudent(data)

  postStudentWorkbookProgressStatus = async (params: req.StudentWorkbookProgressChange) => {
    // const params = this.adapter.personProgress.to(selected)
    const {
      data: { data },
    } = await studentWorkbookApi.postStudentWorkbookProgressStatus(params)
    return data
  }

  patchStudentWorkbookProblemScoring = async (data: req.StudentWorkbookScoring[]) =>
    await studentWorkbookApi.patchWorkbookResult(data)
}

export const lessonWorkbookController = new LessonWorkbookController()
